import { formatDate } from '@angular/common';
import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  CardTemperatureEnum,
  ConfirmationComponent,
  Customer,
  CustomerService,
  HEALTH_BAND_CONTEXT,
  HEALTH_BAND_LABEL,
  Pager,
  QUALIFICATION_CONTEXT,
  QUALIFICATION_LABEL,
  TEMPERATURE_LABEL,
} from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, finalize, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { CustomerFormModalComponent } from './customer-form-modal/customer-form-modal.component';

@Component({
    selector: 'app-customer',
    templateUrl: './customer.component.html',
    styleUrl: './customer.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerComponent extends AbstractComponent {
  public pager: Pager<Customer> = new Pager<Customer>({ perPage: 10 });
  public listObservable: Observable<Pager<Customer>>;
  private searchTerm: Subject<string> = new Subject<string>();

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private customerService: CustomerService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.setupObservableSearch();
    this.getList();
  }

  getList(): void {
    this.loadingContent = true;

    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    this.customerService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingContent = false)
      )
      .subscribe({
        next: (pager: Pager<Customer>) => {
          this.pager = pager;
        },
      });


  }

  search(termo: string) {
    this.pager.page = 1;
    this.searchString = termo;
    this.searchTerm.next(termo);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  add(): void {
    this.dialog.open(CustomerFormModalComponent, { width: '60vw', height: '90vh', data: {} })
      .afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: Customer) => {
        this.getList();
      });
  }

  edit(customer: Customer): void {
    this.dialog.open(CustomerFormModalComponent, { width: '1000px', data: { customer } })
      .afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
        this.getList();
      });
  }

  delete(customer: Customer): void {
    const dialogRef = this.dialog.open(ConfirmationComponent,{ width: '500px' });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (Boolean(result) === true) {
          this.customerService.delete(customer._id)
            .pipe(takeUntil(this.destroy$)).subscribe({
              next: () => {
                this.alertService.success(this.t('kanban.customer.deleteSuccess'));
                this.getList();
              },
              error: (error) => { this.alertService.error(error.error.message) }
            });
        }
      });
  }


  // ---------------------------------------------------------------------------
  // Row readings — the same label and colour maps the board uses (lib-trend-core kanban types)
  // ---------------------------------------------------------------------------

  /** CPF or CNPJ by the digits the account holds. */
  documentLabel(customer: Customer): string {
    return `${customer?.tin ?? ''}`.replace(/\D/g, '').length > 11 ? 'CNPJ' : 'CPF';
  }

  documentMask(customer: Customer): string {
    return this.documentLabel(customer) === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00';
  }

  profileOf(customer: Customer): string {
    return [customer?.segment, customer?.employeeBand].filter(Boolean).join(' · ');
  }

  /** The primary plus the linked people. */
  contactsCount(customer: Customer): number {
    return (customer?.contact ? 1 : 0) + (customer?.contacts?.length ?? 0);
  }

  qualificationLabel(customer: Customer): string {
    return QUALIFICATION_LABEL[customer?.deals?.qualification] ?? 'kanban.qualification.unqualified';
  }

  qualificationContext(customer: Customer): string {
    return QUALIFICATION_CONTEXT[customer?.deals?.qualification] ?? 'secondary';
  }

  healthLabel(customer: Customer): string {
    return HEALTH_BAND_LABEL[customer?.deals?.healthBand] ?? 'kanban.healthBand.stable';
  }

  healthContext(customer: Customer): string {
    return HEALTH_BAND_CONTEXT[customer?.deals?.healthBand] ?? 'secondary';
  }

  temperatureLabel(customer: Customer): string {
    return TEMPERATURE_LABEL[customer?.deals?.temperature as CardTemperatureEnum] ?? 'kanban.cardDetails.temperatureUnavailable';
  }

  /** Same glyphs as the deal card footer, in the icon set this screen already uses. */
  temperatureIcon(customer: Customer): string {
    switch (customer?.deals?.temperature) {
      case CardTemperatureEnum.HOT: return 'mdi mdi-fire text-danger';
      case CardTemperatureEnum.WARM: return 'mdi mdi-weather-partly-cloudy text-dark text-opacity-50';
      case CardTemperatureEnum.COLD: return 'mdi mdi-snowflake text-info';
      default: return 'mdi mdi-thermometer text-muted';
    }
  }

  /** Subscribed once, from `ngOnInit` — a refresh must not add another search stream. */
  setupObservableSearch() {
    this.listObservable = this.searchTerm
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(switchMap(term => {
        return this.customerService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
      }));
    this.listObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe((pager: Pager<Customer>) => {
        this.pager = pager;
      });
  }
  /**
   * Manual lead markings (RF-17, RF-18, RF-37).
   *
   * They live on the CUSTOMER and moved here from the contacts list on 2026-08-21:
   * marking a lead is enrichment and enrichment belongs to the CRM record, not to the
   * messaging identity. Setting one from a deal card would still be wrong for the
   * original reason — it would silently affect every other deal of the same lead — so
   * this list, which is already where commercial judgement about a client happens, is
   * where they belong.
   *
   * Deliberately NOT the attendance chat header, where "block contact" lives: that one
   * blocks a channel and is an attendance action.
   */

  /** Out-of-profile is a judgement about commercial fit, not a daily action. */
  canMarkOutOfIcp(): boolean {
    return this.isAdmin() || this.isSupervisor();
  }

  /** E3 ADR-002: the marking lives on the primary contact, so the row reads it from there. */
  isBlacklisted(customer: Customer): boolean {
    return customer?.contact?.blacklisted === true;
  }

  toggleBlacklist(customer: Customer): void {
    const next = !this.isBlacklisted(customer);

    // RF-22/AC-15: applying withdraws every deal of every account this person is primary
    // of — the count is stated before confirming. Lifting needs no figure.
    const confirm = (deals: number | null) => {
      const message = next
        ? this.t('kanban.customer.blacklistConfirm', { name: customer?.name })
          + (deals !== null ? ` ${this.t('kanban.customer.blacklistImpact', { deals })}` : '')
        : this.t('kanban.customer.blacklistLiftConfirm', { name: customer?.name });

      this.confirmLeadFlag(message, () =>
        this.customerService.setBlacklisted(customer._id, next).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(
              this.t(next ? 'kanban.customer.blacklistSuccess' : 'kanban.customer.blacklistLiftSuccess'),
            );
          },
          error: err => this.alertService.error(err?.error?.message),
        }),
      );
    };

    if (!next) {
      confirm(null);
      return;
    }
    this.customerService.getBlacklistImpact(customer._id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => confirm(result?.deals ?? 0),
      error: () => confirm(null),
    });
  }

  /** E3 RF-30: whether the profile marking came from the company's rule or from a person. */
  icpSourceLabel(customer: Customer): string {
    return customer?.outOfIcpSource === 'RULE' ? 'kanban.customer.icpSourceRule' : 'kanban.customer.icpSourceManual';
  }

  /** The badge tooltip: the marking's provenance and, for a person's, who set it and when (AC-22). */
  icpTooltip(customer: Customer): string {
    const parts = [this.t('kanban.customer.outOfIcpBadgeTooltip'), this.t(this.icpSourceLabel(customer))];
    const author = customer?.outOfIcpBy;
    const name = typeof author === 'object' ? author?.name : undefined;

    if (customer?.outOfIcpSource !== 'RULE' && name) {
      const at = customer.outOfIcpAt
        ? formatDate(customer.outOfIcpAt, 'dd/MM/yyyy HH:mm', this.translateService.currentLang || 'pt-BR')
        : '';
      parts.push(this.t('kanban.customer.icpSourceManualBy', { user: name, date: at }));
    }

    return parts.join(' · ');
  }

  toggleOutOfIcp(customer: Customer): void {
    const next = customer?.outOfIcp !== true;
    const message = next
      ? this.t('kanban.customer.outOfIcpConfirm', { name: customer?.name })
      : this.t('kanban.customer.outOfIcpLiftConfirm', { name: customer?.name });

    this.confirmLeadFlag(message, () =>
      this.customerService.setOutOfIcp(customer._id, next).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.getList();
          this.alertService.success(
            this.t(next ? 'kanban.customer.outOfIcpSuccess' : 'kanban.customer.outOfIcpLiftSuccess'),
          );
        },
        error: err => this.alertService.error(err?.error?.message),
      }),
    );
  }

  /**
   * Both markings change what a whole team sees on the board, so both confirm first —
   * and the message names the lead, because these are triggered from a row menu where
   * mis-clicking the neighbouring row is easy.
   */
  private confirmLeadFlag(message: string, onConfirm: () => void): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, { width: '500px', data: message });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (Boolean(result) === true) onConfirm();
    });
  }

}
